// Communicates with the Overwolf client APIs.
// This module is only meaningful when the page is running inside the Overwolf
// client, where the global `overwolf` object is injected onto `window` (including
// in the embedded iframe).
//
// The native access itself lives in the shared OWProfile wrapper
// (@ow/shared/overwolf/ow-profile); this module keeps the website's historical
// function/type API and delegates to it. Presence detection is
// isOverwolfAvailable() — callers use it instead of touching window.overwolf.

import {
  OWProfile,
  OverwolfProfileResult,
  OverwolfMachineInfo,
} from '@ow/shared/overwolf/ow-profile';

// The signed-in client user. Kept as a local alias so existing website imports
// (`OverwolfProfile`) keep resolving.
export type OverwolfProfile = OverwolfProfileResult;
export type { OverwolfMachineInfo };

// ---------------------------------------------------------------------------
export const isOverwolfAvailable = (): boolean => OWProfile.isAvailable();

// ---------------------------------------------------------------------------
export const getOverwolfVersion = (): string | null => OWProfile.getVersion();

// ---------------------------------------------------------------------------
// The currently signed-in client user, or null when the API is unavailable or
// reports no signed-in user — so callers can treat it as a plain
// "are we logged in?" check.
export const getCurrentOverwolfUser =
  async (): Promise<OverwolfProfile | null> => {
    const result = await OWProfile.getCurrentUser();
    return result?.success ? result : null;
  };

// ---------------------------------------------------------------------------
// The device identifiers the client reports, read regardless of login success
// so analytics can attribute events even when logged out.
export const getOverwolfMachineInfo = (): Promise<OverwolfMachineInfo> =>
  OWProfile.getMachineInfo();

// ---------------------------------------------------------------------------
// Subscribes to client login-state changes so we can re-read the profile after
// the user signs in / out via the native overlay. Returns an unsubscribe
// function; a no-op when the API isn't available.
export const subscribeOverwolfLoginStateChanged = (
  listener: () => void,
): (() => void) => OWProfile.onLoginStateChanged(listener);
